import React from "react";
import ReactDOM from "react-dom/client";

import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "./authConfig";
import axios from "axios";
import { ixicoTransformers } from "lib/common/axios";
import Preload from "components/sign-in-loading/Preload";
import { addAuthEventCallback } from "lib/auth";
import reportWebVitals from "./reportWebVitals";
import { trackWebVitalsEvent, reactPlugin } from "services/azure/app-insights";
import { AppInsightsContext, AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import { RootBoundaryError } from "components/error-boundary/RootBoundaryError";
import { IIdTokenClaims } from "services/msal";

const App = React.lazy(() => import("./App"));

axios.defaults.transformRequest = ixicoTransformers();

export const msalInstance = new PublicClientApplication(msalConfig);

const accounts = msalInstance.getAllAccounts();

/**
 * If the user is signed-in with more than one account (or custom policy), set the active account.
 * The "accounts" array can have 2 items if the user has signed-in both via the sign-in custom policy AND the form completion (MFA) custom policy.
 * In this case, it is required to set the sign-in policy account as the active account (the account with the ACR claim "b2c_1a_v1_mfachoicesignin").
 * This prevents the user from being prompted to sign-in again when attempting to open a new tab with their existing session after completing MFA.
 * Note: If the user's session cookie has expired, the user will still be prompted to sign-in again when opening new tabs.
 * This is the expected behaviour for session cookie expiry when MSAL is configured to use sessionStorage for its cache,
 * as sessionStorage means the MSAL tokens are not shared between tabs.
 */
if (accounts.length > 0) {
  // eslint-disable-next-line
  console.log("Found more than one active account", JSON.stringify(accounts));
  const signInAccount = accounts.find(account => (account.idTokenClaims as IIdTokenClaims)?.acr === "b2c_1a_v1_mfachoicesignin");
  if (!signInAccount) {
    console.warn("No account found with the expected ACR claim.");
  }
  else {
    // eslint-disable-next-line
    console.log("Found account with expected ACR claim", JSON.stringify(signInAccount));
    msalInstance.setActiveAccount(signInAccount);
  }
}

addAuthEventCallback(msalInstance);

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  // StrictMode is disabled for now as this causes development-time issues with useEffect and associated code (e.g. queries and list navigation components).
  // To enable StrictMode without these issues would require some refactoring.
  // <React.StrictMode>
  <React.Suspense fallback={<Preload/>}>
    <AppInsightsContext.Provider value={reactPlugin}>
      <AppInsightsErrorBoundary appInsights={reactPlugin} onError={RootBoundaryError}>
        <App pca={msalInstance} />
      </AppInsightsErrorBoundary>
    </AppInsightsContext.Provider>
  </React.Suspense>
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(trackWebVitalsEvent);
